/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  CompanyContainer,
  HeaderTitle,
  StyledContainer,
  TableCell,
  TableWrapper,
  IconButton,
  LoaderWrapper,
} from '../../../services/settings/appStyle/global.style';

import {
  Table,
  IconButton as IconButtonWithText,
  DeleteConfirmationModal,
  Loader,
  DropdownButton,
} from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import { useCompany } from '../../../hooks/CompanyContext';
import GeofenceService, {
  listAllGeofencesByCompanyAsDistributor,
} from '../../../services/bioplus-services/geofence';
import {
  DeleteIcon,
  // DownloadIcon,
  EditIcon,
  EyeIcon,
  WhiteLocationIcon,
} from '../../../Components/icons';
import moment from 'moment';
import EditGeofenceModal from './modals/editGeofenceModal';
import { ViewGeofenceDetailsModal } from './modals';
import { HeaderButtonWrapper } from './geofence.style';
import { IntlMessages } from '../../../helper/langHelper';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
// import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { useNavigate } from 'react-router-dom';
import { ViewAlertReceivers } from '../PremiseV2/modals';

export function extractData(dataArray) {
  if (dataArray && dataArray.length > 0) {
    const extractedData = dataArray?.map((item) => {
      return {
        GeofenceName: item?.name,
        CompanyName: item?.premiseId?.propertyName,
        DeclarationType: item?.declarationType,
        speed: item?.speedLimit,
        Creator: item?.createdBy,
        createdAt: moment(item?.createdAt).format('DD-MM-YYYY'),
      };
    });
    return { bioplusUsers: extractedData };
  }
}

const PartnerGeofence = () => {
  const userContext = useUserContext();
  const companyContext = useCompany();
  const companyFilter = companyContext?.company?._id;
  // const partnerId = userContext.company.companyId?.id;
  const partnerId = userContext.company?.partnerId;
  const userRole = userContext.company;
  const queryClient = useQueryClient();

  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find(
    (permission) => permission?.label === 'geofence'
  );

  const canDownloadReport = !!isUserAllowToManage?.isReport;
  const canManageGeofence = !!isUserAllowToManage?.isManage;
  // Added: create permission, used only for the new View Map button
  const canCreateGeofence = !!isUserAllowToManage?.isCreate;

  const [editGeofence, setEditGeofence] = useState();
  const [viewGeofence, setViewGeofence] = useState();
  const [deleteGeofence, setDeleteGeofence] = useState();
  const [viewAlertReceivers, setViewAlertReceivers] = useState(undefined);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [name, setName] = useState();

  const navigate = useNavigate();

  const geofenceQuery = useQuery(
    ['partnerGeofences', partnerId, companyFilter, name, page, pageSize],
    () =>
      GeofenceService.listByPartner(
        partnerId,
        { companyId: companyFilter, name, occupants: true },
        page,
        pageSize
      )
  );

  const getAllGeofencesForReport = useQuery(
    ['allGeofencesForReportAsDistributor', companyFilter],
    () => listAllGeofencesByCompanyAsDistributor(partnerId, companyFilter)
  );

  const deleteMutation = useMutation({
    mutationFn: (geofence) => {
      return GeofenceService.partnerGeofenceRemove(geofence?._id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partnerGeofences'] });
      alert('Geofence deleted successfully');
    },
  });

  const geofenceTableData = geofenceQuery?.data?.data ?? [];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleDeleteGeofence = (geofence) => {
    deleteMutation.mutate(geofence);
    setDeleteGeofence(undefined);
  };

  const handleNavigationToMap = (data) => {
    if (data) {
      navigate('/geofenceMap', { state: { data } });
    }
  };

  const canShowAlertReceivers = canManageGeofence && !!companyFilter;

  //   table coulmns
  const userColumns = useMemo(() => {
    const cols = [
      {
        Header: <IntlMessages id="table.col.geofence" />,
        accessor: 'name',
        width: companyFilter ? '15%' : '18%',
      },
      {
        Header: <IntlMessages id="table.col.premise" />,
        accessor: 'premiseId.propertyName',
        Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
        width: companyFilter ? '13%' : '15%',
      },
      {
        Header: <IntlMessages id="table.col.dec.type" />,
        accessor: 'declarationType',
        Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
        width: companyFilter ? '5%' : '10%',
      },
      {
        Header: <IntlMessages id="table.col.speed.limit" />,
        accessor: 'speedLimit',
        Cell: (cellData) => <TableCell>{cellData.value ? cellData.value : '--'}</TableCell>,
        width: '10%',
      },

      {
        Header: <IntlMessages id="table.col.creator" />,
        accessor: 'createdBy',
        width: '21%',
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        width: '15%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
    ];

    if (canShowAlertReceivers) {
      cols.push({
        Header: <IntlMessages id="table.col.alert.receivers" />,
        accessor: 'alertReceiverCount',
        width: '12%',
        Cell: (cellData) => (
          <TableCell
            onClick={() => setViewAlertReceivers(cellData?.row?.original)}
            style={{ display: 'flex', gap: '6px' }}
          >
            <span style={{ alignSelf: 'center' }}>{cellData.value}</span>
            <EyeIcon style={{ width: '22px', height: '22px', cursor: 'pointer' }} />
          </TableCell>
        ),
      });
    }

    // Added: actions column, same gating as the View Map button (isManage + companyFilter)
    if (canManageGeofence && companyFilter) {
      cols.push({
        Header: <IntlMessages id="table.col.actions" />,
        accessor: 'actions',
        isAction: true,
        width: '20%',
      });
    }
    return cols;
  }, [canManageGeofence, companyFilter, canShowAlertReceivers]);

  // Added: same gate used for the actions column and the actionList below
  const canShowEditDelete = canManageGeofence && !!companyFilter;

  const actionList = [
    ...(canShowEditDelete
      ? [
          (data) => (
            <IconButton onClick={() => handleNavigationToMap(data)}>
              <EditIcon style={{ width: '18px', height: '18px' }} />
            </IconButton>
          ),
          (data) => (
            <IconButton onClick={() => setDeleteGeofence(data)}>
              <DeleteIcon style={{ width: '20px', height: '20px' }} />
            </IconButton>
          ),
        ]
      : []),
  ];

  const excelFormatData = extractData(getAllGeofencesForReport?.data);
  const fileName = `Geofences-${userRole?.companyId?.name}-${moment().format(
    'DD-MM-YYYY-hh.mm A'
  )}.xlsx`;
  const fileNameCsv = `Geofences-${userRole?.companyId?.name}-${moment().format(
    'DD-MM-YYYY-hh.mm A'
  )}.csv`;

  const columnConfig = [
    { header: 'Geofence Name', key: 'GeofenceName', width: 30 },
    { header: 'Property Name', key: 'CompanyName', width: 30 },
    { header: 'Declaration Type', key: 'DeclarationType', width: 20 },
    { header: 'Speed Limit', key: 'speed', width: 10 },
    { header: 'Creator', key: 'Creator', width: 30 },
    { header: 'Created Date', key: 'createdAt', width: 20 },
  ];

  const handleClickItem = (item) => {
    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  if (geofenceQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  // Added: View Map button only shows up when isCreate is true AND a company is selected.
  const canShowViewMap = canCreateGeofence && !!companyFilter;

  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.geofences" />
        </HeaderTitle>
        <HeaderButtonWrapper>
          {excelFormatData && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                // { key: 'pdf', title: 'Export as PDF', icon: <PictureAsPdfIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
          {canShowViewMap && (
            <IconButtonWithText
              text={<IntlMessages id="button.text.viewMap" />}
              icon={<WhiteLocationIcon style={{ width: '20px', height: '19px' }} />}
              onClick={() => navigate('/geofenceMap', { state: { isView: true } })}
            />
          )}
        </HeaderButtonWrapper>
      </CompanyContainer>
      <TableWrapper>
        <Table
          data={geofenceTableData}
          columns={userColumns}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={geofenceQuery?.data?.total || 0}
          actions={actionList}
        />
      </TableWrapper>
      {editGeofence && editGeofence !== undefined && (
        <EditGeofenceModal
          onClose={() => setEditGeofence(undefined)}
          data={editGeofence}
          isPartner={true}
        />
      )}
      {viewGeofence && viewGeofence !== undefined && (
        <ViewGeofenceDetailsModal onClose={() => setViewGeofence(undefined)} data={viewGeofence} />
      )}
      {deleteGeofence && (
        <DeleteConfirmationModal
          onClose={() => setDeleteGeofence(undefined)}
          message={
            <IntlMessages
              id="geofence.delete.confirmation"
              values={{ geofence: deleteGeofence.name }}
            />
          }
          onConfirm={() => handleDeleteGeofence(deleteGeofence)}
        />
      )}

      {viewAlertReceivers && viewAlertReceivers !== undefined && (
        <ViewAlertReceivers
          onClose={() => setViewAlertReceivers(undefined)}
          premise={viewAlertReceivers}
          companyId={companyFilter}
          invalidateQuery="partnerGeofences"
        />
      )}
    </StyledContainer>
  );
};

export default PartnerGeofence;
